import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
  postData,
  patchData,
  deleteData,
} from "../../../store/actions";
import {
  getAllDesignations,
  designationDetailUrl,
} from "../../../Service/Apis";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { Designation } from "./Types/DesignationTypes";

interface DesignationDetailProps {
  mode?: "create" | "edit";
}

const DesignationDetail: React.FC<DesignationDetailProps> = ({ mode }) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isEditMode = mode === "edit" || !!params.id;
  const designationId = params.id;

  const [loading, setLoading] = useState<boolean>(isEditMode);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);

  const [designation, setDesignation] = useState<Partial<Designation>>({
    designation_name: "",
    designation_description: "",
  });

  const handleShowToast = (
    message: string,
    variant: ToastVariant,
    isCloseable: boolean
  ) => {
    showToast(message, variant, isCloseable);
  };

  // Load existing designation in edit mode
  useEffect(() => {
    if (!isEditMode || !designationId) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = await dispatch(
          fetchData(designationDetailUrl(designationId))
        );
        setDesignation(data as Designation);
      } catch (error: any) {
        handleShowToast(
          error?.response?.data?.message || "Failed to load designation.",
          "error",
          true
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [dispatch, designationId, isEditMode]);

  const handleChange =
    (field: keyof Designation) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDesignation((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSave = async () => {
    if (!designation.designation_name || designation.designation_name.trim() === "") {
      handleShowToast("Role name is required.", "error", true);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        designation_name: designation.designation_name?.trim(),
        designation_description: designation.designation_description?.trim() || null,
      };

      if (isEditMode && designationId) {
        await dispatch(patchData(designationDetailUrl(designationId), payload));
        handleShowToast("Role updated successfully.", "success", true);
      } else {
        const created = await dispatch(postData(getAllDesignations, payload));
        handleShowToast("Role created successfully.", "success", true);
        
      }

      navigate("/pms/designations");
    } catch (error: any) {
      handleShowToast(
        error?.response?.data?.message ||
          "Failed to save designation. Please try again.",
        "error",
        true
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!designationId) return;
    try {
      setDeleting(true);
      await dispatch(deleteData(designationDetailUrl(designationId)));
      handleShowToast("Designation deleted successfully.", "success", true);
      navigate("/pms/designations");
    } catch (error: any) {
      handleShowToast(
        error?.response?.data?.message ||
          "Failed to delete Role. Please resolve dependencies and try again.",
        "error",
        true
      );
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10 }}>
        <Typography>Loading role...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 12, mb: 5 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/pms/designations")}
        sx={{ mb: 2 }}
      >
        Back to Roles
      </Button>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, mt: 1, mb: 2 }}
      >
        {isEditMode ? "Edit Role" : "Create New Role"}
      </Typography>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}
      >
        <TextField
          label="Role Name"
          value={designation.designation_name || ""}
          onChange={handleChange("designation_name")}
          required
          fullWidth
        />

        <TextField
          label="Description"
          value={designation.designation_description || ""}
          onChange={handleChange("designation_description")}
          fullWidth
          multiline
          minRows={3}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/pms/designations")}
            disabled={saving || deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Box sx={{ display: "flex", gap: 2 }}>
            {isEditMode && (
              <Button
                color="error"
                variant="outlined"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={saving || deleting}
                sx={{ textTransform: "none" }}
              >
                Delete Role
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || deleting}
              sx={{ textTransform: "none"}}
            >
              {isEditMode ? "Save Changes" : "Create Role"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you really want to delete the Role "
            {designation.designation_name}"? This action cannot be undone and
            may fail if the role is in use by employees or forms.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DesignationDetail;